/**
 * Il vocabolario degli stati del Cockpit, in un posto solo.
 *
 * **Tono, glifo e parola viaggiano insieme.** Circa un operatore su dodici non
 * distingue rosso, arancio e giallo: uno stato affidato al solo colore, per lui,
 * non esiste. E' la stessa regola delle scale di gravita' (§ 03), applicata a
 * cio' che riguarda la sessione invece del difetto.
 *
 * **La frase contiene l'azione.** «Conflitto» dice all'operatore che qualcosa e'
 * andato storto e non che cosa deve fare del candidato che ha davanti; qui ogni
 * stato porta il recupero dentro il testo, perche' e' l'unico punto in cui
 * l'operatore lo legge.
 *
 * Un elenco unico e non una frase scritta dove serve: due copie divergono alla
 * prima modifica, e la seconda si scopre il giorno in cui un operatore legge
 * due nomi diversi per lo stesso stato.
 */

import { tx } from '../i18n/t'
import type { IntentState } from '../outbox'

/** Come si nomina uno stato: parola, glifo e gettone del tono. */
export interface Tone {
  label: string
  glyph: string
  /** Il gettone, mai il colore. */
  tone: string
}

export const IN_ATTESA: Tone = {
  get label() {
    return tx('cockpit.statePending')
  },
  glyph: '◇',
  tone: 'ink-3',
}

export const INTENT_TONES: Record<IntentState, Tone> = {
  decision_pending: {
    get label() {
      return tx('cockpit.stateDecisionPending')
    },
    glyph: '◆',
    tone: 'acc',
  },
  applied: {
    get label() {
      return tx('cockpit.stateApplied')
    },
    glyph: '✓',
    tone: 'sev-0',
  },
  withdrawn: {
    get label() {
      return tx('cockpit.stateWithdrawn')
    },
    glyph: '↺',
    tone: 'sev-2',
  },
  rejected: {
    get label() {
      return tx('cockpit.stateRejected')
    },
    glyph: '■',
    tone: 'sev-4',
  },
  stale: {
    get label() {
      return tx('cockpit.stateStale')
    },
    glyph: '▲▲',
    tone: 'sev-3',
  },
}

export const RINVIATO: Tone = {
  get label() {
    return tx('cockpit.stateDeferred')
  },
  glyph: '○',
  tone: 'sev-1',
}

export const OFFLINE: Tone = {
  get label() {
    return tx('cockpit.stateOffline')
  },
  glyph: '▬',
  tone: 'sev-2',
}

export const DEGRADATO: Tone = {
  get label() {
    return tx('cockpit.stateDegraded')
  },
  glyph: '▲',
  tone: 'sev-3',
}

export const RICONCILIAZIONE: Tone = {
  get label() {
    return tx('cockpit.stateReconciling')
  },
  glyph: '◈',
  tone: 'ref',
}

export const CONTESA: Tone = {
  get label() {
    return tx('cockpit.stateContended')
  },
  glyph: '○',
  tone: 'sev-1',
}

export const ESAURITO: Tone = {
  get label() {
    return tx('cockpit.stateExhausted')
  },
  glyph: '✓',
  tone: 'sev-0',
}

export const CHIUSA: Tone = {
  get label() {
    return tx('cockpit.stateClosed')
  },
  glyph: '✓',
  tone: 'sev-0',
}

/** Lo stato del lease di un candidato, come lo nomina il servizio.
 *
 * I nomi sono quelli di la': tradurli qui vorrebbe dire tenere allineati due
 * vocabolari a mano, e il giorno in cui divergono la differenza si vede come
 * una riga della coda che non si sa spiegare. */
export function leaseLabel(state: string | null | undefined, mine: boolean): string {
  if (mine) return tx('cockpit.leaseMine')
  if (state === 'decision_pending') return tx('cockpit.leasePending')
  if (state === 'leased' || state === 'preleased') return tx('cockpit.leaseOthers')
  if (state === 'released' || state === null || state === undefined) return tx('cockpit.leaseAvailable')
  return state
}
